import { useRef, useState } from "react";
import { motion, useMotionValue, useScroll, useSpring, useTransform } from "framer-motion";
import { ArrowRight, ArrowUpRight, CheckCircle2, Heart, MapPin, Phone } from "lucide-react";
import { Link } from "react-router-dom";
import {
    BENEFIT_SIGNS,
    IMAGES,
    PEDIATRIC_AREAS,
    PHONE_DISPLAY,
    PHONE_TEL,
    PRINCIPLES,
    SERVICES,
    WA_DEFAULT,
    WHY_CHOOSE,
    WORK_AREAS,
} from "../data/site";
import { Eyebrow, MaskedLines, Reveal, SectionHeading } from "../components/Reveal";
import { Icon } from "../components/Icon";
import { Marquee } from "../components/Marquee";
import { Timeline } from "../components/Timeline";
import { ConsultCTA } from "../components/ConsultCTA";
import { usePageMeta } from "../hooks/usePageMeta";

const TINTS = {
    teal: "bg-teal-soft text-teal-dark",
    sky: "bg-sky/15 text-sky",
    peach: "bg-peach-soft text-peach-deep",
};

const Hero = () => {
    const sectionRef = useRef(null);
    const frameRef = useRef(null);
    const mx = useMotionValue(0.5);
    const my = useMotionValue(0.5);
    const sx = useSpring(mx, { stiffness: 60, damping: 18 });
    const sy = useSpring(my, { stiffness: 60, damping: 18 });
    const rotateX = useTransform(sy, [0, 1], [5, -5]);
    const rotateY = useTransform(sx, [0, 1], [-5, 5]);
    const { scrollYProgress } = useScroll({ target: sectionRef, offset: ["start start", "end start"] });
    const imgY = useTransform(scrollYProgress, [0, 1], [0, 90]);

    const onMove = (e) => {
        const r = frameRef.current.getBoundingClientRect();
        mx.set((e.clientX - r.left) / r.width);
        my.set((e.clientY - r.top) / r.height);
    };
    const onLeave = () => {
        mx.set(0.5);
        my.set(0.5);
    };

    return (
        <section ref={sectionRef} className="relative overflow-hidden bg-cream pt-32 md:pt-40" data-testid="home-hero">
            <div
                className="pointer-events-none absolute -left-40 top-20 h-[30rem] w-[30rem] rounded-full opacity-50 blur-3xl"
                style={{ background: "radial-gradient(circle, rgba(114,207,196,0.4) 0%, transparent 70%)" }}
            />
            <div className="relative mx-auto grid max-w-7xl items-center gap-14 px-6 pb-20 md:px-8 lg:grid-cols-12 lg:gap-8">
                <div className="lg:col-span-7">
                    <motion.div
                        initial={{ opacity: 0, y: 18 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 1.35, duration: 0.6 }}
                    >
                        <Eyebrow>Speech &amp; Language Therapy · Anishabad, Patna</Eyebrow>
                    </motion.div>
                    <h1 className="mt-7 font-jakarta text-[2.9rem] font-extrabold leading-[1.04] tracking-tight text-navy sm:text-6xl lg:text-7xl xl:text-[5.2rem]">
                        <MaskedLines
                            lines={["Better Communication."]}
                            delay={1.45}
                        />
                        <MaskedLines
                            lines={["Greater Confidence."]}
                            delay={1.6}
                            className="text-teal"
                        />
                    </h1>
                    <motion.p
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 1.85, duration: 0.7 }}
                        className="mt-7 max-w-xl text-base leading-relaxed text-ink/70 md:text-lg"
                    >
                        Welcome to Speech Horizon, where we help children and adults improve their speech,
                        language, communication, and social interaction skills through personalized and
                        goal-oriented therapy.
                    </motion.p>
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 2.0, duration: 0.7 }}
                        className="mt-9 flex flex-wrap items-center gap-4"
                    >
                        <Link
                            to="/contact"
                            data-testid="hero-book-consultation-btn"
                            className="group inline-flex items-center gap-2 rounded-full bg-peach px-8 py-4 font-jakarta text-sm font-bold text-navy shadow-xl shadow-peach/30 transition-all duration-300 hover:-translate-y-1 hover:bg-peach-deep hover:text-cream"
                        >
                            Book a Consultation
                            <ArrowUpRight size={17} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                        </Link>
                        <a
                            href={WA_DEFAULT}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="hero-whatsapp-btn"
                            className="inline-flex items-center gap-2 rounded-full border-2 border-teal/30 bg-white/60 px-8 py-[14px] font-jakarta text-sm font-bold text-teal-dark backdrop-blur transition-all duration-300 hover:-translate-y-1 hover:border-teal hover:bg-teal hover:text-cream"
                        >
                            <Phone size={16} /> Call / WhatsApp
                        </a>
                    </motion.div>
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 2.2, duration: 0.8 }}
                        className="mt-10 flex flex-wrap items-center gap-x-7 gap-y-3 text-sm font-semibold text-ink/65"
                    >
                        <span className="flex items-center gap-2">
                            <MapPin size={16} className="text-teal" /> Anishabad, Patna
                        </span>
                        <a
                            href={PHONE_TEL}
                            data-testid="hero-phone-link"
                            className="flex items-center gap-2 transition-colors hover:text-teal"
                        >
                            <Phone size={16} className="text-teal" /> {PHONE_DISPLAY}
                        </a>
                        <span className="flex items-center gap-2">
                            <span className="animate-pulse-dot h-2 w-2 rounded-full bg-teal" /> Now accepting consultations
                        </span>
                    </motion.div>
                </div>

                <div className="lg:col-span-5" style={{ perspective: 1200 }}>
                    <motion.div
                        initial={{ opacity: 0, y: 40, scale: 0.96 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        transition={{ delay: 1.6, duration: 1, ease: [0.22, 1, 0.36, 1] }}
                    >
                        <div
                            ref={frameRef}
                            onMouseMove={onMove}
                            onMouseLeave={onLeave}
                            className="relative mx-auto max-w-md"
                        >
                            <div className="spotlight pointer-events-none absolute -inset-16 rounded-full" />
                            <motion.div
                                style={{ rotateX, rotateY, transformStyle: "preserve-3d" }}
                                className="relative"
                            >
                                <div className="relative overflow-hidden rounded-b-[2.5rem] rounded-t-[11rem] border-[6px] border-white shadow-2xl shadow-navy/20">
                                    <motion.img
                                        src={IMAGES.hero}
                                        alt="Speech therapist engaging with a child during a warm communication therapy session"
                                        style={{ y: imgY }}
                                        className="h-[430px] w-full scale-110 object-cover md:h-[540px]"
                                        data-testid="hero-image"
                                    />
                                    <div className="absolute inset-0 bg-gradient-to-t from-navy/25 via-transparent to-transparent" />
                                </div>
                                <div
                                    className="animate-float absolute -left-6 bottom-12 flex items-center gap-3 rounded-2xl bg-white/90 p-4 shadow-xl shadow-navy/10 backdrop-blur md:-left-12"
                                    style={{ transform: "translateZ(50px)" }}
                                >
                                    <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-peach-soft text-peach-deep">
                                        <Heart size={19} />
                                    </span>
                                    <div>
                                        <p className="font-jakarta text-sm font-bold text-navy">Child-Friendly Sessions</p>
                                        <p className="text-xs text-ink/60">Warm, playful &amp; goal-oriented</p>
                                    </div>
                                </div>
                                <div
                                    className="animate-float absolute -right-3 top-16 rounded-full bg-navy px-5 py-2.5 font-jakarta text-xs font-bold text-cream shadow-lg md:-right-8"
                                    style={{ transform: "translateZ(70px)", animationDelay: "1.4s" }}
                                >
                                    1:1 Individual Attention
                                </div>
                            </motion.div>
                        </div>
                    </motion.div>
                </div>
            </div>
        </section>
    );
};

const IntroCurtain = () => {
    const [show, setShow] = useState(true);
    if (!show) return null;
    return (
        <motion.div
            className="fixed inset-0 z-[80] flex items-center justify-center bg-navy"
            initial={{ y: 0 }}
            animate={{ y: "-100%" }}
            transition={{ duration: 0.9, delay: 1.0, ease: [0.76, 0, 0.24, 1] }}
            onAnimationComplete={() => setShow(false)}
            aria-hidden="true"
        >
            <motion.img
  initial={{ opacity: 0, y: 18 }}
  animate={{ opacity: 1, y: 0 }}
  transition={{ delay: 0.15, duration: 0.55 }}
  src="/Images/Logo_Transparent_White.png"
  alt="Speech Horizon"
  className="h-16 w-auto"
/>
        </motion.div>
    );
};

const ServiceCard = ({ service, index }) => (
    <Reveal delay={index * 0.07}>
        <div
            className="group h-full rounded-[1.75rem] border border-navy/5 bg-white p-8 transition-all duration-500 hover:-translate-y-2 hover:border-teal/25 hover:shadow-2xl hover:shadow-teal/10"
            data-testid={`service-card-${service.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}
        >
            <span
                className={`inline-flex h-14 w-14 items-center justify-center rounded-2xl transition-transform duration-500 group-hover:rotate-6 group-hover:scale-110 ${TINTS[service.tint]}`}
            >
                <Icon name={service.icon} size={24} strokeWidth={1.9} />
            </span>
            <h3 className="mt-6 font-jakarta text-xl font-bold text-navy">{service.title}</h3>
            <p className="mt-3 text-sm leading-relaxed text-ink/65">{service.description}</p>
            <Link
                to={service.link}
                data-testid={`service-learn-more-${service.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                className="mt-6 inline-flex items-center gap-1.5 text-sm font-bold text-teal-dark transition-colors hover:text-peach-deep"
            >
                Learn More
                <ArrowUpRight size={15} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
        </div>
    </Reveal>
);

const Home = () => {
    usePageMeta(
        "Speech Horizon | Speech Therapist in Patna (Anishabad) — Speech & Language Therapy",
        "Speech Horizon is a speech & language therapy practice in Anishabad, Patna helping children and adults with speech delay, autism communication, stammering, articulation and voice. Call 9973416270."
    );

    return (
        <div data-testid="home-page">
            <IntroCurtain />
            <Hero />
            <Marquee />

            <section className="bg-cream py-20 md:py-28" data-testid="home-about-teaser">
                <div className="mx-auto grid max-w-7xl items-center gap-14 px-6 md:px-8 lg:grid-cols-2">
                    <Reveal className="relative order-2 lg:order-1">
                        <div className="absolute -left-5 -top-5 h-full w-full rotate-[-2deg] rounded-[2.5rem] bg-teal-soft" />
                        <img
                            src="/Images/Aman.jpeg"
                            alt="Parent and child sharing a warm moment of communication"
                            className="relative h-[420px] w-full rounded-[2.5rem] object-cover object-[center_75%] shadow-xl shadow-navy/10 md:h-[500px]"
                        />
                        <div className="absolute -bottom-6 right-6 rounded-2xl bg-navy px-6 py-4 text-cream shadow-xl">
                            <p className="font-jakarta text-lg font-extrabold">Aman</p>
                            <p className="text-xs text-aqua">Speech Therapist</p>
                        </div>
                    </Reveal>
                    <div className="order-1 lg:order-2">
                        <SectionHeading
                            align="left"
                            eyebrow="About Us"
                            title="Meet Your Speech Therapist"
                        />
                        <Reveal delay={0.2}>
                            <div className="mt-6 space-y-4 text-base leading-relaxed text-ink/70">
                                <p>
                                    Namaste! I&rsquo;m Aman, a professional Speech Therapist dedicated to helping
                                    children and adults overcome speech, language, communication, and related
                                    difficulties.
                                </p>
                                <p>
                                    Every individual has unique needs, strengths, and abilities. That is why
                                    therapy is carefully planned around individual needs and specific goals.
                                </p>
                            </div>
                            <Link
                                to="/about"
                                data-testid="home-about-link"
                                className="group mt-8 inline-flex items-center gap-2 font-jakarta text-sm font-bold text-teal-dark transition-colors hover:text-peach-deep"
                            >
                                More About Our Practice
                                <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
                            </Link>
                        </Reveal>
                    </div>
                </div>
            </section>

            <section className="bg-mist py-20 md:py-28" data-testid="home-services-section">
                <div className="mx-auto max-w-7xl px-6 md:px-8">
                    <SectionHeading
                        eyebrow="What We Do"
                        title="Our Speech Therapy Services"
                        description="Personalized, goal-oriented therapy for children and adults — planned around each individual's needs, strengths, and abilities."
                    />
                    <div className="mt-14 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                        {SERVICES.map((service, i) => (
                            <ServiceCard key={service.title} service={service} index={i} />
                        ))}
                    </div>
                </div>
            </section>

            <section className="bg-cream py-20 md:py-28" data-testid="home-pediatric-section">
                <div className="mx-auto max-w-5xl px-6 md:px-8">
                    <SectionHeading
                        eyebrow="For Children"
                        title="Pediatric Speech & Communication Therapy"
                        description="We provide therapy support for children experiencing difficulties associated with:"
                    />
                    <Reveal delay={0.15}>
                        <div className="mt-12 flex flex-wrap justify-center gap-3" data-testid="pediatric-areas-chips">
                            {PEDIATRIC_AREAS.map((area) => (
                                <span
                                    key={area}
                                    className="cursor-default rounded-full border border-teal/20 bg-white px-5 py-2.5 text-sm font-semibold text-navy shadow-sm transition-all duration-300 hover:-translate-y-0.5 hover:border-teal hover:bg-teal hover:text-cream"
                                >
                                    {area}
                                </span>
                            ))}
                        </div>
                    </Reveal>
                </div>
            </section>

            <section className="bg-mist py-20 md:py-28" data-testid="home-work-areas-section">
                <div className="mx-auto max-w-7xl px-6 md:px-8">
                    <SectionHeading
                        eyebrow="Areas of Focus"
                        title="What We Work On"
                        description="Therapy at Speech Horizon supports the whole picture of communication — from first words to confident conversation."
                    />
                    <div className="mt-14 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                        {WORK_AREAS.map((area, i) => (
                            <Reveal key={area.title} delay={i * 0.06}>
                                <div className="group h-full rounded-[1.75rem] border border-navy/5 bg-white p-7 transition-all duration-500 hover:-translate-y-2 hover:shadow-xl hover:shadow-sky/10">
                                    <div className="flex items-center gap-4">
                                        <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-sky/10 text-sky transition-transform duration-500 group-hover:scale-110">
                                            <Icon name={area.icon} size={21} strokeWidth={1.9} />
                                        </span>
                                        <h3 className="font-jakarta text-lg font-bold text-navy">{area.title}</h3>
                                    </div>
                                    <p className="mt-4 text-sm leading-relaxed text-ink/65">{area.description}</p>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            <section className="relative overflow-hidden bg-navy py-20 md:py-28" data-testid="home-approach-section">
                <div
                    className="pointer-events-none absolute -right-40 top-0 h-[28rem] w-[28rem] rounded-full opacity-20 blur-3xl"
                    style={{ background: "radial-gradient(circle, #2A9D8F 0%, transparent 70%)" }}
                />
                <div className="relative mx-auto max-w-6xl px-6 md:px-8">
                    <SectionHeading
                        dark
                        eyebrow="Our Approach"
                        title="Every Person Is Different. Every Therapy Plan Should Be Different."
                        description="Before beginning therapy, it is important to understand an individual's communication needs, strengths, and areas of difficulty. We aim to make therapy:"
                    />
                    <div className="mx-auto mt-14 grid max-w-4xl gap-x-16 md:grid-cols-2">
                        {PRINCIPLES.map((principle, i) => (
                            <Reveal key={principle} delay={i * 0.07}>
                                <div className="group flex items-baseline gap-6 border-t border-white/10 py-6 transition-all duration-500 hover:translate-x-2">
                                    <span className="font-jakarta text-3xl font-extrabold text-aqua/40 transition-colors duration-500 group-hover:text-peach">
                                        {String(i + 1).padStart(2, "0")}
                                    </span>
                                    <span className="font-jakarta text-xl font-bold text-cream md:text-2xl">
                                        {principle}
                                    </span>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                    <Reveal delay={0.2}>
                        <p className="mx-auto mt-12 max-w-3xl text-center text-base leading-relaxed text-cream/70 md:text-lg">
                            Parents are also involved in the therapy process so that practice is not limited to
                            therapy sessions. This helps individuals apply and develop their communication skills
                            in everyday life.
                        </p>
                    </Reveal>
                </div>
            </section>

            <section className="bg-cream py-20 md:py-28" data-testid="home-why-choose-section">
                <div className="mx-auto max-w-7xl px-6 md:px-8">
                    <SectionHeading
                        eyebrow="The Speech Horizon Difference"
                        title="Why Choose Speech Horizon?"
                    />
                    <div className="mt-14 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                        {WHY_CHOOSE.map((item, i) => (
                            <Reveal key={item.title} delay={i * 0.06}>
                                <div className="group h-full rounded-[1.75rem] border border-navy/5 bg-white p-8 transition-all duration-500 hover:-translate-y-2 hover:border-peach/40 hover:shadow-2xl hover:shadow-peach/10">
                                    <span className="inline-flex h-14 w-14 items-center justify-center rounded-full bg-peach-soft text-peach-deep transition-transform duration-500 group-hover:rotate-6 group-hover:scale-110">
                                        <Icon name={item.icon} size={22} strokeWidth={1.9} />
                                    </span>
                                    <h3 className="mt-5 font-jakarta text-lg font-bold text-navy">{item.title}</h3>
                                    <p className="mt-2.5 text-sm leading-relaxed text-ink/65">{item.description}</p>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            <section className="bg-mist py-20 md:py-28" data-testid="home-timeline-section">
                <div className="mx-auto max-w-7xl px-6 md:px-8">
                    <SectionHeading
                        eyebrow="The Process"
                        title="How Therapy Works"
                        description="A clear, structured journey — from first assessment to confident everyday communication."
                    />
                    <Timeline />
                </div>
            </section>

            <section className="bg-cream py-20 md:py-28" data-testid="home-benefit-section">
                <div className="mx-auto grid max-w-7xl items-center gap-14 px-6 md:px-8 lg:grid-cols-2">
                    <div>
                        <SectionHeading
                            align="left"
                            eyebrow="Is It Time?"
                            title="Who Can Benefit From Speech Therapy?"
                            description="Speech-language assessment and professional consultation may be beneficial if your child:"
                        />
                        <Reveal delay={0.15}>
                            <ul className="mt-8 space-y-3.5" data-testid="benefit-signs-list">
                                {BENEFIT_SIGNS.map((sign) => (
                                    <li key={sign} className="flex items-start gap-3 text-[15px] leading-relaxed text-ink/75">
                                        <CheckCircle2 size={19} className="mt-0.5 shrink-0 text-teal" />
                                        {sign}
                                    </li>
                                ))}
                            </ul>
                        </Reveal>
                        <Reveal delay={0.2}>
                            <p className="mt-8 text-base leading-relaxed text-ink/70">
                                If you notice any of these concerns, a professional speech-language assessment can
                                help identify your child&rsquo;s communication needs and determine appropriate
                                therapy support.
                            </p>
                            <Link
                                to="/contact"
                                data-testid="benefit-book-consultation-btn"
                                className="group mt-7 inline-flex items-center gap-2 rounded-full bg-teal px-8 py-4 font-jakarta text-sm font-bold text-cream shadow-xl shadow-teal/25 transition-all duration-300 hover:-translate-y-1 hover:bg-teal-dark"
                            >
                                Book a Consultation
                                <ArrowUpRight size={17} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                            </Link>
                        </Reveal>
                    </div>
                    <Reveal className="relative">
                        <img
                            src={IMAGES.learning}
                            alt="Child engaged in a structured learning and communication activity"
                            className="h-[440px] w-full rounded-[2.5rem] object-cover shadow-xl shadow-navy/10 md:h-[560px]"
                        />
                        <div className="absolute -bottom-7 left-6 max-w-xs rounded-2xl bg-white p-5 shadow-xl shadow-navy/10 md:left-10">
                            <p className="font-jakarta text-sm font-bold leading-snug text-navy">
                                &ldquo;Early support makes a meaningful difference in communication
                                development.&rdquo;
                            </p>
                        </div>
                    </Reveal>
                </div>
            </section>

            <section className="bg-peach-soft/60 py-20 md:py-28" data-testid="home-parent-role-section">
                <div className="mx-auto grid max-w-7xl items-center gap-14 px-6 md:px-8 lg:grid-cols-2">
                    <div>
                        <SectionHeading
                            align="left"
                            eyebrow="Family First"
                            title="Parent's Role in Therapy"
                        />
                        <Reveal delay={0.12}>
                            <div className="mt-6 space-y-4 text-base leading-relaxed text-ink/70">
                                <p>Parents play an important role in the speech therapy process.</p>
                                <p>
                                    Everyday activities can provide valuable opportunities for children to
                                    practice communication skills at home.
                                </p>
                            </div>
                        </Reveal>
                        <Reveal delay={0.2}>
                            <div className="mt-8 rounded-2xl border-l-4 border-peach-deep bg-white p-6 shadow-lg shadow-peach/10">
                                <p className="font-jakarta text-lg font-extrabold leading-snug text-navy md:text-xl">
                                    Small Daily Practice + Consistent Support ={" "}
                                    <span className="text-teal">Better Communication Opportunities</span>
                                </p>
                            </div>
                        </Reveal>
                        <Reveal delay={0.28}>
                            <p className="mt-7 text-base leading-relaxed text-ink/70">
                                Based on the therapy goals, parents may be provided with practical home activities
                                and ideas to encourage communication development in everyday situations.
                            </p>
                            <Link
                                to="/parent-guidance"
                                data-testid="parent-role-guidance-link"
                                className="group mt-6 inline-flex items-center gap-2 font-jakarta text-sm font-bold text-teal-dark transition-colors hover:text-peach-deep"
                            >
                                Explore Parent Guidance
                                <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
                            </Link>
                        </Reveal>
                    </div>
                    <Reveal className="relative">
                        <div className="absolute -right-5 -top-5 h-full w-full rotate-[2deg] rounded-[2.5rem] bg-white/70" />
                        <img
                            src={IMAGES.family}
                            alt="A father playing joyfully with his two children outdoors"
                            className="relative h-[420px] w-full rounded-[2.5rem] object-cover shadow-xl shadow-navy/10 md:h-[520px]"
                        />
                    </Reveal>
                </div>
            </section>

            <section
                className="relative overflow-hidden py-24 md:py-32"
                style={{ background: "linear-gradient(135deg, #2A9D8F 0%, #1F7A70 55%, #173B4D 100%)" }}
                data-testid="home-goal-section"
            >
                <div
                    className="pointer-events-none absolute -left-32 -top-32 h-96 w-96 rounded-full opacity-30 blur-3xl"
                    style={{ background: "radial-gradient(circle, #72CFC4 0%, transparent 70%)" }}
                />
                <div className="relative mx-auto max-w-4xl px-6 text-center">
                    <Reveal>
                        <Eyebrow dark>Our Goal</Eyebrow>
                    </Reveal>
                    <Reveal delay={0.1}>
                        <h2 className="mt-6 font-jakarta text-4xl font-extrabold leading-[1.08] tracking-tight text-cream sm:text-5xl lg:text-6xl">
                            Better Communication.
                            <span className="block text-peach">Greater Confidence.</span>
                        </h2>
                    </Reveal>
                    <Reveal delay={0.2}>
                        <p className="mx-auto mt-7 max-w-2xl text-base leading-relaxed text-cream/85 md:text-lg">
                            Our goal is not simply to improve words or speech sounds. We aim to support
                            individuals in communicating effectively, expressing their needs and feelings,
                            participating in social interactions, and developing greater confidence in everyday
                            life.
                        </p>
                    </Reveal>
                </div>
            </section>

            <ConsultCTA />
        </div>
    );
};

export default Home;
